import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Plus, Search, Tag } from "lucide-react";
import { toast } from "@/hooks/use-toast";

// Custom hooks
import {
  useProductAttributes,
  useProductAttributeValues,
  useCreateProductAttribute,
  useUpdateProductAttribute,
  useDeleteProductAttribute,
  useCreateProductAttributeValue,
  useUpdateProductAttributeValue,
  useDeleteProductAttributeValue,
} from "@/services/product-attribute-service";

// Components
import { ProductAttributeCard } from "@/components/product-attributes/ProductAttributeCard";
import { ProductAttributeFormDialog } from "@/components/product-attributes/ProductAttributeFormDialog";
import { ProductAttributeValueFormDialog } from "@/components/product-attributes/ProductAttributeValueFormDialog";
import { ProductAttributeValuesTable } from "@/components/product-attributes/ProductAttributeValuesTable";

// Types and schemas
import {
  ProductAttributeCreateFormValues,
  ProductAttributeUpdateFormValues,
  ProductAttributeValueCreateFormValues,
  ProductAttributeValueUpdateFormValues,
} from "@/types/product-attribute-type";
import type { ProductAttributeSortOption } from "@/api/types";
import { AttributeValue, ProductAttribute } from "@/types/product-type";

export default function ProductAttributesPage() {
  const [activeTab, setActiveTab] = useState("attributes");
  const [selectedAttribute, setSelectedAttribute] = useState<ProductAttribute | null>(null);
  const [selectedValue, setSelectedValue] = useState<AttributeValue | null>(null);

  // Form states
  const [isAddingAttribute, setIsAddingAttribute] = useState(false);
  const [isEditingAttribute, setIsEditingAttribute] = useState(false);
  const [isAddingValue, setIsAddingValue] = useState(false);
  const [isEditingValue, setIsEditingValue] = useState(false);

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSize, setCurrentSize] = useState<number>(20);
  const [currentSort, setCurrentSort] = useState<ProductAttributeSortOption>('id,asc');

  // Hooks
  const {
    items: attributes,
    isLoading: attributesLoading,
    totalElements,
    totalPages,
    isFirst,
    isLast,
    refresh: refetchAttributes
  } = useProductAttributes();

  const {
    items: attributeValues,
    isLoading: valuesLoading,
    refresh: refetchValues
  } = useProductAttributeValues(selectedAttribute?.id || null);

  const createAttributeMutation = useCreateProductAttribute();
  const updateAttributeMutation = useUpdateProductAttribute();
  const deleteAttributeMutation = useDeleteProductAttribute();
  const createValueMutation = useCreateProductAttributeValue();
  const updateValueMutation = useUpdateProductAttributeValue();
  const deleteValueMutation = useDeleteProductAttributeValue();

  // Handlers
  const handleCreateAttribute = async (values: ProductAttributeCreateFormValues) => {
    try {
      await createAttributeMutation.executeAsync(values);
      toast({
        title: "Success",
        description: "Product attribute created successfully",
      });
      setIsAddingAttribute(false);
      refetchAttributes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create product attribute",
        variant: "destructive",
      });
    }
  };

  const handleUpdateAttribute = async (values: ProductAttributeUpdateFormValues) => {
    if (!selectedAttribute) return;
    try {
      await updateAttributeMutation.executeAsync({
        id: String(selectedAttribute.id),
        data: values,
      });
      toast({
        title: "Success",
        description: "Product attribute updated successfully",
      });
      setIsEditingAttribute(false);
      setSelectedAttribute(null);
      refetchAttributes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update product attribute",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAttribute = async (id: string) => {
    const idStr = String(id);
    try {
      await deleteAttributeMutation.executeAsync(idStr);
      toast({
        title: "Success",
        description: "Product attribute deleted successfully",
      });
      if (String(selectedAttribute?.id) === idStr) {
        setSelectedAttribute(null);
        setActiveTab("attributes");
      }
      refetchAttributes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete product attribute",
        variant: "destructive",
      });
    }
  };

  const handleCreateValue = async (values: ProductAttributeValueCreateFormValues) => {
    if (!selectedAttribute) return;
    try {
      await createValueMutation.executeAsync({
        ...values,
        attributeId: selectedAttribute.id,
      });
      toast({
        title: "Success",
        description: "Attribute value created successfully",
      });
      setIsAddingValue(false);
      refetchValues();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create attribute value",
        variant: "destructive",
      });
    }
  };

  const handleUpdateValue = async (values: ProductAttributeValueUpdateFormValues) => {
    if (!selectedValue) return;
    try {
      await updateValueMutation.executeAsync({
        id: selectedValue.id,
        data: values,
        attributeId: selectedAttribute?.id,
      });
      toast({
        title: "Success",
        description: "Attribute value updated successfully",
      });
      setIsEditingValue(false);
      setSelectedValue(null);
      refetchValues();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update attribute value",
        variant: "destructive",
      });
    }
  };

  const handleDeleteValue = async (id: string) => {
    try {
      await deleteValueMutation.executeAsync({
        id,
        attributeId: selectedAttribute?.id,
      });
      toast({
        title: "Success",
        description: "Attribute value deleted successfully",
      });
      refetchValues();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete attribute value",
        variant: "destructive",
      });
    }
  };

  const handleEditAttribute = (attribute: ProductAttribute) => {
    setSelectedAttribute(attribute);
    setIsEditingAttribute(true);
  };

  const handleEditValue = (value: AttributeValue) => {
    setSelectedValue(value);
    setIsEditingValue(true);
  };

  const handleManageValues = (attributeId: string) => {
    const attribute = attributes.find(attr => String(attr.id) === String(attributeId));
    if (attribute) {
      setSelectedAttribute(attribute);
      setActiveTab("values");
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
    refetchAttributes();
  };

  const handleSortChange = (sort: string) => {
    setCurrentSort(sort as ProductAttributeSortOption);
    setCurrentPage(0);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (size: number) => {
    setCurrentSize(size);
    setCurrentPage(0);
  };

  // Reset forms when dialogs close
  const handleAttributeDialogClose = (open: boolean) => {
    if (!open) {
      setIsAddingAttribute(false);
      setIsEditingAttribute(false);
      setSelectedAttribute(null);
    }
  };

  const handleValueDialogClose = (open: boolean) => {
    if (!open) {
      setIsAddingValue(false);
      setIsEditingValue(false);
      setSelectedValue(null);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-700">Product Attributes</h1>
        <Button
          onClick={() => setIsAddingAttribute(true)}
          className="gap-2"
        >
          <Plus size={16} />
          <span>Add Attribute</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="attributes">Attributes</TabsTrigger>
          <TabsTrigger value="values" disabled={!selectedAttribute}>
            Attribute Values
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attributes" className="space-y-4">
          {/* Search and Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Search & Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Search Attributes</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="search"
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch} size="sm">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="w-full sm:w-48">
                  <Label htmlFor="sort">Sort By</Label>
                  <Select value={currentSort} onValueChange={handleSortChange}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="id,asc">ID (Ascending)</SelectItem>
                      <SelectItem value="id,desc">ID (Descending)</SelectItem>
                      <SelectItem value="name,asc">Name (A-Z)</SelectItem>
                      <SelectItem value="name,desc">Name (Z-A)</SelectItem>
                      <SelectItem value="displayName,asc">Display Name (A-Z)</SelectItem>
                      <SelectItem value="displayName,desc">Display Name (Z-A)</SelectItem>
                      <SelectItem value="createdAt,asc">Created (Oldest First)</SelectItem>
                      <SelectItem value="createdAt,desc">Created (Newest First)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-32">
                  <Label htmlFor="size">Page Size</Label>
                  <Select value={currentSize.toString()} onValueChange={(value) => handleSizeChange(Number(value))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attributes List */}
          {attributesLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
            </div>
          ) : attributes.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Settings className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Attributes</h3>
                <p className="text-gray-500 mb-4 text-center max-w-md">
                  You haven't created any product attributes yet. Attributes help you define product variations like color and size.
                </p>
                <Button onClick={() => setIsAddingAttribute(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Add First Attribute
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {attributes.map((attribute) => (
                <ProductAttributeCard
                  key={attribute.id}
                  attribute={attribute}
                  onEdit={handleEditAttribute}
                  onDelete={handleDeleteAttribute}
                  onManageValues={handleManageValues}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {currentPage * currentSize + 1} to {Math.min((currentPage + 1) * currentSize, totalElements)} of {totalElements} results
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={isFirst}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={isLast}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="values" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Attribute Values</CardTitle>
                  <CardDescription>
                    {selectedAttribute ? `Manage values for ${selectedAttribute.displayName || selectedAttribute.name}` : "Select an attribute to manage its values"}
                  </CardDescription>
                </div>
                {selectedAttribute && (
                  <Button onClick={() => setIsAddingValue(true)} className="gap-2">
                    <Plus size={16} />
                    <span>Add Value</span>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedAttribute ? (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Please select an attribute from the Attributes tab to manage its values
                  </p>
                </div>
              ) : (
                <ProductAttributeValuesTable
                  values={attributeValues as AttributeValue[]}
                  attributeName={selectedAttribute.displayName || selectedAttribute.name}
                  onEdit={handleEditValue}
                  onDelete={handleDeleteValue}
                  isLoading={valuesLoading}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Attribute Form Dialog */}
      <ProductAttributeFormDialog
        open={isAddingAttribute || isEditingAttribute}
        onOpenChange={handleAttributeDialogClose}
        onSubmit={isEditingAttribute ? handleUpdateAttribute : handleCreateAttribute}
        attribute={selectedAttribute}
        isLoading={false}
        productId={"0"} />

      {/* Attribute Value Form Dialog */}
      <ProductAttributeValueFormDialog
        open={isAddingValue || isEditingValue}
        onOpenChange={handleValueDialogClose}
        onSubmit={isEditingValue ? handleUpdateValue : handleCreateValue}
        value={isEditingValue ? selectedValue : undefined}
        attributeName={selectedAttribute?.displayName || selectedAttribute?.name}
        isLoading={false}
      />
    </div>
  );
}
